import jsPDF from 'jspdf';
import logo from '../images/logo.png';
import {
  formatMerchShippingAddress,
  flattenSocioRecord,
  getMerchSocioCustomerName,
  resolveMerchCheckoutSocio,
} from './merchFulfillmentLabels';

async function getBase64ImageFromURL(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function formatReceiptDate(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function formatVariantSummary(selectedVariants = {}) {
  const entries = Object.entries(selectedVariants || {}).filter(
    ([, value]) => value
  );
  if (entries.length === 0) return null;
  return entries.map(([key, value]) => `${key}: ${value}`).join(' · ');
}

function addRow(doc, label, value, xLabel, xValue, y) {
  doc.text(`${label}:`, xLabel, y);
  doc.text(String(value || '—'), xValue, y);
}

function formatEuro(amount) {
  return `€ ${Number(amount || 0).toFixed(2)}`;
}

function formatPaymentMethod(method) {
  if (method === 'contanti') return 'Contanti';
  if (method === 'punti') return 'Punti CIL';
  if (method === 'elettronico') return 'Pagamento elettronico';
  return method || 'Pagamento elettronico';
}

function getProductLineAmount(summary) {
  const discountAmount = Number(summary?.discountAmount) || 0;
  const productTotal = Number(summary?.productTotal) || 0;
  const productSubtotal = Number(summary?.productSubtotal) || 0;
  const originalAmount = Number(summary?.originalAmount) || 0;

  if (discountAmount > 0) {
    return productSubtotal || productTotal + discountAmount || originalAmount;
  }

  return productTotal || productSubtotal || originalAmount;
}

function addProductAmountRow(
  doc,
  productName,
  amount,
  margin,
  xValue,
  y,
  lineHeight
) {
  const label = String(productName || 'Prodotto').trim() || 'Prodotto';
  const labelMaxWidth = xValue - margin - 12;
  const labelLines = doc.splitTextToSize(`${label}:`, labelMaxWidth);
  const rowHeight = Math.max(lineHeight, labelLines.length * lineHeight);

  labelLines.forEach((line, index) => {
    doc.text(line, margin, y + index * lineHeight);
  });
  doc.text(formatEuro(amount), xValue, y);

  return rowHeight;
}

export function isMerchOrderRecord(order) {
  return Boolean(order?.metadata?.merch);
}

function getShippingRecipientName(shippingAddress) {
  return [shippingAddress?.nome, shippingAddress?.cognome]
    .filter(Boolean)
    .join(' ')
    .trim();
}

export function resolveMerchReceiptCustomer({
  order = null,
  summary = null,
  userData = null,
}) {
  const merch = order?.metadata?.merch || summary?.metadata?.merch || {};
  const orderSocio = flattenSocioRecord(order?.socio);
  let customerName = getMerchSocioCustomerName(orderSocio);

  if (!customerName && userData?.tesseramenti?.length) {
    const socioId =
      orderSocio?.documentId ||
      orderSocio?.id ||
      summary?.participantId ||
      (typeof order?.socio === 'string' || typeof order?.socio === 'number'
        ? order.socio
        : null);

    const matchedSocio = userData.tesseramenti.find((socio) => {
      const flat = flattenSocioRecord(socio);
      return (
        socioId &&
        (flat?.documentId === socioId || String(flat?.id) === String(socioId))
      );
    });

    customerName = getMerchSocioCustomerName(matchedSocio);
  }

  if (!customerName) {
    customerName = getMerchSocioCustomerName(
      resolveMerchCheckoutSocio(userData, summary?.participantId)
    );
  }

  if (!customerName && order) {
    customerName = [order.customerFirstName, order.customerLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  if (!customerName) {
    customerName = getShippingRecipientName(
      summary?.shippingAddress || merch.shippingAddress
    );
  }

  if (!customerName && userData) {
    customerName = [userData.firstName, userData.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  const email =
    orderSocio?.email ||
    order?.customerEmail ||
    userData?.email ||
    summary?.customerEmail ||
    '';

  return {
    customerName,
    email,
  };
}

function buildMerchReceiptCustomer(order, merch, userData = null) {
  return resolveMerchReceiptCustomer({ order, summary: null, userData });
}

export function buildMerchOrderReceiptPayload(order, userData = null) {
  const merch = order?.metadata?.merch || {};
  const products = Array.isArray(order?.metadata?.products)
    ? order.metadata.products
    : [];
  const productLine =
    products.find((product) => product?.type === 'product') ||
    products[0] ||
    {};

  const productName =
    order?.product?.nome ||
    order?.product?.attributes?.nome ||
    productLine.name ||
    'Prodotto';

  const shippingFee = Number(merch.shippingFee) || 0;
  const productTotal = Number(merch.productTotal ?? productLine.amount) || 0;
  const productSubtotal = Number(merch.productSubtotal) || 0;
  const discountAmount =
    Number(order?.metadata?.discountAmount ?? productLine.discount?.amount) ||
    0;
  const originalAmount =
    Number(order?.metadata?.originalAmount ?? productLine.originalAmount) || 0;
  const total = Number(order?.price) || productTotal + shippingFee;

  const summary = {
    documentId: order?.documentId,
    orderDocumentId: order?.documentId,
    createdAt: order?.createdAt,
    productName,
    isPreorder: merch.isPreorder === true,
    fulfillmentMethod: merch.fulfillmentMethod || 'pickup',
    shippingAddress: merch.shippingAddress || null,
    selectedVariants: productLine.variants || {},
    productTotal,
    productSubtotal,
    originalAmount,
    discountAmount,
    shippingFee,
    total,
    paymentIntentId: order?.paymentIntentId || null,
    paymentMethod: order?.paymentMethod || null,
  };

  return {
    summary,
    customer: buildMerchReceiptCustomer(order, merch, userData),
  };
}

export async function generateMerchOrderReceiptPdf(summary, customer = {}) {
  let logoData = '';
  try {
    logoData = await getBase64ImageFromURL(logo);
  } catch {
    // logo opzionale
  }

  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'pt', 'a4');
  const { width: pageWidth, height: pageHeight } = doc.internal.pageSize;
  const margin = 40;
  const lineHeight = 18;
  const xValue = margin + 150;
  let y = 40;

  const giallo = '#ffcc00';
  const blu = '#36a9e1';
  const viola = '#3b235a';

  doc.setFillColor(giallo);
  doc.rect(0, 0, pageWidth, 120, 'F');
  if (logoData) {
    doc.addImage(logoData, 'PNG', pageWidth - margin - 100, 20, 100, 35);
  }

  doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor('#000');
  doc.text('CIL – Creatività in Libertà APS', margin, 30);
  doc.setFont('helvetica', 'normal');
  doc.text('CF/P.IVA: 94090260012', margin, 45);
  doc.text('Via GB Buschetti 18, 10023 Chieri (TO)', margin, 60);
  doc.text('info@cilassociazione.it | +39 353 468 2493', margin, 75);
  doc.text('www.cilassociazione.it', margin, 90);

  y = 140;
  doc
    .setDrawColor(blu)
    .setLineWidth(1)
    .line(margin, y, pageWidth - margin, y);
  y += 28;

  doc.setFontSize(16).setFont('helvetica', 'bold').setTextColor(viola);
  doc.text('Ricevuta ordine SostieniCIL', pageWidth / 2, y, {
    align: 'center',
  });
  y += 32;

  doc.setFontSize(12).setFont('helvetica', 'normal').setTextColor('#000');

  const customerName = String(customer.customerName || '').trim();
  const orderDocumentId = String(
    summary?.orderDocumentId || summary?.documentId || ''
  ).trim();
  const rows = [
    ['Data', formatReceiptDate(summary?.createdAt)],
    ['N. ordine', orderDocumentId || '—'],
    ['Cliente', customerName || '—'],
    ['Email', customer.email || '—'],
    ['Prodotto', summary?.productName || '—'],
    ['Tipo ordine', summary?.isPreorder ? 'Prenotazione' : 'Acquisto'],
    [
      'Consegna',
      summary?.fulfillmentMethod === 'shipping'
        ? 'Spedizione a domicilio'
        : 'Ritiro in sede / evento',
    ],
  ];

  const variantSummary = formatVariantSummary(summary?.selectedVariants);
  if (variantSummary) {
    rows.push(['Varianti', variantSummary]);
  }

  rows.forEach(([label, value]) => {
    addRow(doc, label, value, margin, xValue, y);
    y += lineHeight;
  });

  if (summary?.fulfillmentMethod === 'shipping' && summary?.shippingAddress) {
    y += 6;
    addRow(
      doc,
      'Destinatario',
      formatMerchShippingAddress(summary.shippingAddress),
      margin,
      xValue,
      y
    );
    y += lineHeight * 2;
  }

  y += 10;
  doc.setDrawColor(blu).line(margin, y, pageWidth - margin, y);
  y += 24;

  doc.setFont('helvetica', 'bold');
  doc.text('Dettaglio importi', margin, y);
  y += lineHeight + 4;
  doc.setFont('helvetica', 'normal');

  const discountAmount = Number(summary?.discountAmount) || 0;
  y += addProductAmountRow(
    doc,
    summary?.productName,
    getProductLineAmount(summary),
    margin,
    xValue,
    y,
    lineHeight
  );

  if (discountAmount > 0) {
    addRow(doc, 'Sconto', `- ${formatEuro(discountAmount)}`, margin, xValue, y);
    y += lineHeight;
  }

  if (summary?.shippingFee > 0) {
    addRow(
      doc,
      'Spedizione',
      formatEuro(summary.shippingFee),
      margin,
      xValue,
      y
    );
    y += lineHeight;
  }

  doc.setFont('helvetica', 'bold');
  addRow(doc, 'Totale pagato', formatEuro(summary?.total), margin, xValue, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  addRow(
    doc,
    'Metodo',
    formatPaymentMethod(summary?.paymentMethod),
    margin,
    xValue,
    y
  );

  if (summary?.paymentIntentId) {
    y += lineHeight;
    addRow(doc, 'Rif. pagamento', summary.paymentIntentId, margin, xValue, y);
  }

  y += 30;
  doc.setDrawColor(blu).line(margin, y, pageWidth - margin, y);
  y += 24;

  doc.setFont('helvetica', 'italic').setFontSize(11).setTextColor('#444444');
  const note =
    'Documento generato automaticamente a conferma del pagamento. Per assistenza: info@cilassociazione.it';
  const noteLines = doc.splitTextToSize(note, pageWidth - margin * 2);
  noteLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, y + index * lineHeight, { align: 'center' });
  });

  doc.setFillColor(viola);
  doc.rect(0, pageHeight - 50, pageWidth, 50, 'F');
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor('#ffffff');
  doc.text(
    'Grazie per aver scelto CIL – Creatività in Libertà APS',
    pageWidth / 2,
    pageHeight - 25,
    { align: 'center' }
  );

  const filenameBase = orderDocumentId || 'merch';
  const filename = `ricevuta_merch_${filenameBase}.pdf`;

  return { doc, filename };
}

export async function printMerchOrderReceipt(
  summary,
  customer = {},
  { download = false } = {}
) {
  const { doc, filename } = await generateMerchOrderReceiptPdf(
    summary,
    customer
  );

  if (download) {
    doc.save(filename);
    return;
  }

  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  const printWindow = window.open(blobUrl, '_blank');
  if (!printWindow) {
    doc.save(filename);
    return;
  }

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

export async function downloadMerchOrderReceiptFromOrder(
  order,
  userData = null
) {
  const { summary, customer } = buildMerchOrderReceiptPayload(order, userData);
  await printMerchOrderReceipt(summary, customer, { download: true });
}
